import React, { useEffect, useState } from 'react';
import { ThemedPlot } from './ThemedPlot';
import { usePlotTheme } from '../../hooks/usePlotTheme';
import type { Sample } from '../../types';
import { getRockTypeColor, getVEIColor } from '../../utils/colors';
import { getMatchMethod, getMatchMethodLabel, getVolcanoName } from '../../utils/matchMethod';
import { useIsPhone, PHONE_LEGEND, chartTitle } from '../../hooks/useIsPhone';

interface TASPlotProps {
  /** Array of samples to plot */
  samples: Sample[];
  /** Whether to show loading state */
  loading?: boolean;
  /** Color mode: 'rock_type' or 'vei' */
  colorBy?: 'rock_type' | 'vei';
  /** Custom title for the plot */
  title?: string;
}

interface TASPolygon {
  name: string;
  coordinates: number[][];
}

interface TASData {
  polygons: TASPolygon[];
  alkali_line: {
    name: string;
    coordinates: number[][];
  };
  axes: {
    x: { label: string; range: [number, number] };
    y: { label: string; range: [number, number] };
  };
}

/**
 * TAS (Total Alkali-Silica) Plot Component
 * 
 * Displays a TAS classification diagram with:
 * - Classification polygons (basalt, andesite, rhyolite, etc.)
 * - Alkali/Subalkalic dividing line
 * - Sample points colored by rock type
 */
export const TASPlot: React.FC<TASPlotProps> = React.memo(({
  samples,
  loading = false,
  colorBy = 'rock_type',
  title,
}) => {
  const isPhone = useIsPhone();
  const { palette } = usePlotTheme();
  const [tasData, setTasData] = useState<TASData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch TAS polygon data
  useEffect(() => {
    const fetchTASData = async () => {
      try {
        const response = await fetch('/api/analytics/tas-polygons', {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
          },
        });
        if (!response.ok) throw new Error('Failed to fetch TAS data');
        const data = await response.json();
        setTasData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      }
    };

    fetchTASData();
  }, []);

  // Process sample data for plotting
  const prepareSampleData = () => {
    return samples
      .filter(s => s.oxides?.['SIO2'] && s.oxides?.['NA2O'] && s.oxides?.['K2O'])
      .map(s => {
        const method = getMatchMethod(s.matching_metadata);
        const methodLabel = getMatchMethodLabel(method);

        return {
          sio2: s.oxides!['SIO2']!,
          alkali: s.oxides!['NA2O']! + s.oxides!['K2O']!,
          material: s.material || 'Unknown',
          rock_type: s.petro?.rock_type || 'Unknown',
          sample_code: s.sample_code || s.sample_id,
          sample_id: s.sample_id,
          vei: s.vei,
          eruption_date: s.eruption_date,
          volcano_name: getVolcanoName(s.matching_metadata),
          method: method,
          methodLabel: methodLabel,
        };
      });
  };

  const sampleData = prepareSampleData();

  // Material shapes mapping
  const materialShapes: Record<string, string> = {
    'WR': 'circle',
    'GL': 'square',
    'MIN': 'diamond',
    'INC': 'triangle-up',
    'Unknown': 'x',
  };

  const sampleMarkerOutline = {
    color: '#000000',
    width: 0.6,
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-600">Error loading TAS diagram: {error}</div>
      </div>
    );
  }

  if (loading || !tasData) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Loading TAS diagram...</div>
      </div>
    );
  }

  if (sampleData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">No samples with oxide data to plot</div>
      </div>
    );
  }

  // Prepare Plotly data
  const plotlyData: Plotly.Data[] = [];

  // Add classification polygons
  for (const polygon of tasData.polygons) {
    const x = polygon.coordinates.map(coord => coord[0]);
    const y = polygon.coordinates.map(coord => coord[1]);
    
    plotlyData.push({
      type: 'scatter',
      mode: 'lines',
      x,
      y,
      fill: 'toself',
      fillcolor: `rgba(200, 200, 200, 0.1)`,
      line: { color: palette.field, width: 1 },
      hoverinfo: 'text',
      text: polygon.name,
      name: polygon.name,
      showlegend: false,
    });
  }

  // Add alkali/subalkalic line
  const alkaliX = tasData.alkali_line.coordinates.map(coord => coord[0]);
  const alkaliY = tasData.alkali_line.coordinates.map(coord => coord[1]);
  plotlyData.push({
    type: 'scatter',
    mode: 'lines',
    x: alkaliX,
    y: alkaliY,
    line: { color: palette.reference, width: 2, dash: 'dash' },
    name: 'Alkali/Subalkalic',
    showlegend: true,
    hoverinfo: 'name',
  });

  // Grouping strategy: Group by rock_type|material or vei|material
  if (colorBy === 'rock_type') {
    // ROCK TYPE MODE: Group by rock_type|material
    // Legend shows materials (WR, GL, MIN, INC), colors show rock types
    const uniqueRockTypes = Array.from(new Set(sampleData.map(s => s.rock_type)));
    const rockTypeColors: Record<string, string> = {};
    
    for (const rockType of uniqueRockTypes) {
      rockTypeColors[rockType] = getRockTypeColor(rockType);
    }

    const samplesByRockTypeAndMaterial = sampleData.reduce((acc, sample) => {
      const key = `${sample.rock_type}|${sample.material}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(sample);
      return acc;
    }, {} as Record<string, typeof sampleData>);

    const materialLegendShown = new Set<string>();

    for (const [key, samples] of Object.entries(samplesByRockTypeAndMaterial)) {
      const [rockType, material] = key.split('|');
      const shape = materialShapes[material] || materialShapes['Unknown'];
      const color = rockTypeColors[rockType] || '#808080';
      const showLegend = !materialLegendShown.has(material);
      
      if (showLegend) {
        materialLegendShown.add(material);
      }
      
      plotlyData.push({
        type: 'scatter',
        mode: 'markers',
        x: samples.map(s => s.sio2),
        y: samples.map(s => s.alkali),
        name: material,
        legendgroup: material,
        showlegend: showLegend,
        marker: {
          size: 8,
          opacity: 0.7,
          symbol: shape,
          color: color,
          line: sampleMarkerOutline,
        },
        text: samples.map(s => 
          `${s.sample_code}<br>`+
          `Rock Type: ${s.rock_type}<br>`+
          `Material: ${s.material}<br>`+
          `SiO2: ${s.sio2.toFixed(2)}%<br>`+
          `Alkali: ${s.alkali.toFixed(2)}%${s.volcano_name ? `<br>`+
          `Volcano: ${s.volcano_name}` : ''}<br>`+
          `Association: ${s.methodLabel}`
        ),
        hoverinfo: 'text',
      });
    }
  } else if (colorBy === 'vei') {
    // VEI MODE: Group by vei|material
    // Legend shows materials (WR, GL, MIN, INC), colors show VEI
    const uniqueVEIs = Array.from(new Set(sampleData.map(s => s.vei).filter(v => v !== undefined))) as number[];
    const veiColors: Record<string, string> = {};
    
    for (const vei of uniqueVEIs) {
      veiColors[vei.toString()] = getVEIColor(vei);
    }
    // Add color for unknown VEI
    veiColors['Unknown'] = '#808080';

    const samplesByVEIAndMaterial = sampleData.reduce((acc, sample) => {
      const veiKey = sample.vei !== undefined ? sample.vei.toString() : 'Unknown';
      const key = `${veiKey}|${sample.material}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(sample);
      return acc;
    }, {} as Record<string, typeof sampleData>);

    const materialLegendShown = new Set<string>();

    for (const [key, samples] of Object.entries(samplesByVEIAndMaterial)) {
      const [veiKey, material] = key.split('|');
      const shape = materialShapes[material] || materialShapes['Unknown'];
      const color = veiColors[veiKey] || '#808080';
      const showLegend = !materialLegendShown.has(material);
      
      if (showLegend) {
        materialLegendShown.add(material);
      }
      
      plotlyData.push({
        type: 'scatter',
        mode: 'markers',
        x: samples.map(s => s.sio2),
        y: samples.map(s => s.alkali),
        name: material,
        legendgroup: material,
        showlegend: showLegend,
        marker: {
          size: 8,
          opacity: 0.7,
          symbol: shape,
          color: color,
          line: sampleMarkerOutline,
        },
        text: samples.map(s => 
          `${s.sample_code}<br>`+
          `VEI: ${s.vei !== undefined ? s.vei : 'Unknown'}<br>`+
          `Material: ${s.material}<br>`+
          `SiO2: ${s.sio2.toFixed(2)}%<br>`+
          `Alkali: ${s.alkali.toFixed(2)}%${s.volcano_name ? `<br>`+
          `Volcano: ${s.volcano_name}` : ''}<br>`+
          `Association: ${s.methodLabel}`
        ),
        hoverinfo: 'text',
      });
    }
  }

  return (
    <div className="w-full h-full">
      <ThemedPlot
        data={plotlyData}
        layout={{
          title: {
            ...chartTitle(title || `TAS Diagram ${colorBy === 'vei' ? '(Colored by VEI)' : '(Colored by Rock Type)'}`, isPhone),
            font: { size: 16 },
          },
          xaxis: {
            title: { text: 'SiO₂ (wt%)' },
            range: tasData.axes.x.range,
          },
          yaxis: {
            title: { text: 'Na₂O + K₂O (wt%)' },
            range: tasData.axes.y.range,
          },
          autosize: true,
          hovermode: 'closest',
          showlegend: true,
          legend: isPhone ? PHONE_LEGEND : {
            x: 1.05,
            y: 1,
            orientation: 'v',
          },
          margin: isPhone ? { l: 50, r: 16, t: 60, b: 40 } : { l: 60, r: 150, t: 60, b: 60 },
        }}
        config={{
          displayModeBar: true,
          displaylogo: false,
          modeBarButtonsToRemove: ['lasso2d', 'select2d'],
          toImageButtonOptions: {
            format: 'png',
            filename: 'tas_diagram',
            height: 800,
            width: 1000,
          },
        }}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
});
